<template v-if="copy">
  <div
    class="copy-container"
    :class="containAlignObject"
  >
    <div
      v-if="shouldShowTopCopy || showBcomLogo"
      class="promo-copy-block"
    >
      <div
        v-if="isValidTextField(copy?.attributes?.eyebrow)"
        :class="[copy?.attributes?.eyebrowStyleType, 'eyebrow', 'item']"
        :style="copy?.attributes?.eyebrowStyleType==='boxedEyebrow' ? { background: copy?.attributes?.eyebrowBgColor ?? '#000' } : ''"
        v-html="sanitize(copy?.attributes?.eyebrow, cleanStyles)"
      />
      <div
        v-if="showBcomLogo"
        :class="['logo', containAlignObject]"
        :style="`height:${copy?.attributes?.logoContainer}`"
      >
        <PromoSlideshowLogo :logocontext="copy" />
      </div>

      <div
        v-if="isValue2Slide && isValidTextField(copy?.attributes?.heading)"
        class="item heading"
        v-html="sanitize(copy?.attributes?.heading, cleanStyles)"
      />

      <div
        v-if="isValidTextField(copy?.attributes?.heading2)"
        class="item heading2 large-margin-top-xs medium-margin-top-xxs small-margin-top-xxs"
        v-html="sanitize(copy?.attributes?.heading2, cleanStyles)"
      />

      <div
        v-if="
          (!isMarketShareSlide || !isValueSlide) &&
            isValidTextField(copy?.attributes?.subHeading)
        "
        class="item subHeading"
        v-html="sanitize(copy?.attributes?.subHeading, cleanStyles)"
      />

      <div
        v-if="isValueSlide && isValidTextField(copy?.attributes?.body)"
        class="item body margin-top-xxs"
        v-html="sanitize(copy?.attributes?.body, cleanStyles)"
      />

      <div
        v-if="
          isMarketShareSlide &&
            isValueSlide &&
            isValidTextField(copy?.attributes?.subHeading)
        "
        class="item subHeading margin-top-xs"
        v-html="sanitize(copy?.attributes?.subHeading, cleanStyles)"
      />
    </div>
    <div
      v-if="isPromoChartVisible"
      class="promo-chart margin-top-m"
      aria-label="Promo Chart"
    >
      <PromoChart
        :promo-chart="copy?.table"
        :style="{ color: copy?.attributes?.fontColor }"
      />
    </div>
    <div
      v-if="shouldShowBottomCopy"
      class="promo-copy-block"
      :class="{ 'margin-top-m': isPromoChartVisible }"
    >
      <div
        v-if="isValidTextField(copy?.attributes?.body2)"
        class="item body2"
        v-html="sanitize(copy?.attributes?.body2, cleanStyles)"
      />
      <div
        v-if="isValidTextField(copy?.attributes?.subHeading2)"
        class="item subHeading2 margin-top-xs"
        v-html="sanitize(copy?.attributes?.subHeading2, cleanStyles)"
      />

      <div
        v-if="isValidTextField(copy?.attributes?.body3)"
        class="item body3"
        v-html="sanitize(copy?.attributes?.body3, cleanStyles)"
      />

      <div
        v-if="isValidTextField(copy?.attributes?.subHeading3)"
        class="item subHeading3 margin-top-xs"
        v-html="sanitize(copy?.attributes?.subHeading3, cleanStyles)"
      />

      <div
        v-if="isMarketShareSlide && isValidTextField(copy?.attributes?.subBody)"
        class="item subBody"
        v-html="sanitize(copy?.attributes?.subBody, cleanStyles)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { sanitize } from '@vue-util/sanitize';
import { brand } from 'config';
import cleanStyles from '../../helpers/getCleanStyles';
import shouldShowChart from '../../helpers/showPromoChart';
import PromoSlideshowLogo from './PromoSlideshowLogo';
import PromoChart from '../text/PromoChart';
import { isValidTextField } from '../../helpers/cleanHtml';

const props = defineProps({
  copy: {
    type: Object,
    default: () => {},
  },
  brand: String,
});

const textArr = [
  'eyebrow',
  'heading',
  'body2',
  'body3',
  'body',
  'subHeading2',
  'subHeading3',
  'isLogoPresent',
  'subHeading',
  'heading2',
];
const showBcomLogo = ref(
  props.copy?.attributes?.isLogoPresent === 'true' && brand === 'bcom',
);
const filteredAttr = Object.entries(props.copy?.attributes ?? {})
  .filter(([key]) => textArr.includes(key))
  .reduce(
    (filtered, [key, value]) => {
      const hasNumbers = /\d/.test(key);
      if (hasNumbers) {
        filtered.withNums[key] = value;
      } else {
        filtered.withoutNums[key] = value;
      }
      return filtered;
    },
    { withNums: {}, withoutNums: {} },
  );

const shouldShowTopCopy = ref(
  Object.values(filteredAttr.withoutNums).some((item) => isValidTextField(item)),
);
const shouldShowBottomCopy = ref(
  Object.values(filteredAttr.withNums).some((item) => isValidTextField(item)),
);
const isPromoChartVisible = ref(shouldShowChart(props.copy));

const isMarketShareSlide = ref(
  !['market-share-1', 'market-share-2', 'market-share-3', 'market-share-4'].includes(
    props.copy?.attributes?.slideType,
  ),
);
const isValueSlide = ref(
  !['value-1', 'value-2', 'value-3'].includes(props.copy?.attributes?.slideType),
);
const isValue2Slide = ref(props.copy?.attributes?.slideType !== 'value-2');

const containAlignObject = ref({
  'align-right': props.copy?.attributes?.alignment === 'right',
  'align-left': props.copy?.attributes?.alignment === 'left',
  'align-center': props.copy?.attributes?.alignment === 'center',
});

const eyebrowFontColor = props.copy?.attributes?.eyebrowStyleType === 'boxedEyebrow' && props.copy?.attributes?.fontColor === '#000000' && (props.copy?.attributes?.eyebrowBgColor === undefined || null)
  ? 'white' : props.copy?.attributes?.fontColor;

</script>

<style lang="scss" scoped>

.promo-copy-block .eyebrow{
display: inline-block;
color: v-bind(eyebrowFontColor);
}

.copy-container {
  display: flex;
  flex-direction: column;

  .subBody,
  .body2,
  .body3 {
    margin-top: rem-calc(4px);
  }
  .item:first-child {
    margin-top: 0;
  }
  .eyebrow,
  .heading,
  .subHeading,
  .heading2,
  .subHeading2,
  .subHeading3,
  .body,
  .subBody,
  .body2,
  .body3 {
    div {
      color: inherit;
    }
  }
}
@media screen and (min-width: #{map-get($breakpoints, 'medium')}) and (max-width: #{map-get($breakpoints, 'large')-1}) {
  .promo-fashion-1,
  .promo-fashion-2,
  .promo-fashion-3,
  .promo-fashion-4 {
    .copy-container {
      width: 80%;
    }
  }
}
</style>
<style lang="scss" brand="mcom" scoped>
@import "./../../scss/globalAttributes.mcom.scss";
.copy-container,
.promo-copy-block {
  justify-content: center;
}
</style>

<style lang="scss" brand="bcom" scoped>
@import "./../../scss/globalAttributes.bcom.scss";

.copy-container {
  .promo-copy-block:first-of-type {
    div:first-child {
      margin-top: 0;
    }
    .logo {
      display: flex;
    }
  }
}

.eyebrow.boxedEyebrow :deep(> div:first-child) {
  display: inline-block;
  padding: rem-calc(8) rem-calc(20);
}

</style>
